/**
 * Shared countdown timer hook used across all pages (PDP, advertorial, landers).
 * 
 * Cookie-based perpetual urgency timer:
 * - Starts at 1:27:14 on first visit
 * - Counts down every second, persisted via cookie
 * - Resets to 1:27:14 when it reaches the 1:17:00 threshold
 * - This creates a perpetual countdown loop that never expires
 */

import { useState, useEffect, useRef } from "react";

export interface CountdownState {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const COOKIE_NAME = "fluff_timer_end";

// Starting countdown: 1 hour, 27 minutes, 14 seconds
const START_HOURS = 1;
const START_MINUTES = 27;
const START_SECONDS = 14;
const START_TOTAL_MS = (START_HOURS * 3600 + START_MINUTES * 60 + START_SECONDS) * 1000;

// Reset threshold: when timer reaches 1:17:00, reset back to start
const RESET_THRESHOLD_MS = (1 * 3600 + 17 * 60 + 0) * 1000;

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name: string, value: string, days: number = 7) {
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function initializeTimerEndTime(): number {
  const stored = getCookie(COOKIE_NAME);
  if (stored) {
    const endTime = parseInt(stored, 10);
    if (!isNaN(endTime) && endTime > Date.now()) {
      // Check if remaining time is above reset threshold
      const remaining = endTime - Date.now();
      if (remaining > RESET_THRESHOLD_MS) {
        return endTime;
      }
      // Below threshold — reset
    }
  }
  // Set new end time
  const endTime = Date.now() + START_TOTAL_MS;
  setCookie(COOKIE_NAME, endTime.toString());
  return endTime;
}

function calculateCountdown(endTime: number): CountdownState {
  const diff = Math.max(0, endTime - Date.now());
  
  // If we've hit the reset threshold, this will be handled by the effect
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds };
}

export function useSharedCountdown(): CountdownState {
  const endTimeRef = useRef<number>(0);
  const [countdown, setCountdown] = useState<CountdownState>({
    days: 0,
    hours: START_HOURS,
    minutes: START_MINUTES,
    seconds: START_SECONDS,
  });

  useEffect(() => {
    // Initialize end time from cookie or create new
    endTimeRef.current = initializeTimerEndTime();
    setCountdown(calculateCountdown(endTimeRef.current));

    const interval = setInterval(() => {
      const remaining = endTimeRef.current - Date.now();

      // Reset if below threshold or expired
      if (remaining <= RESET_THRESHOLD_MS) {
        const newEndTime = Date.now() + START_TOTAL_MS;
        setCookie(COOKIE_NAME, newEndTime.toString());
        endTimeRef.current = newEndTime;
      }

      setCountdown(calculateCountdown(endTimeRef.current));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return countdown;
}
